/**
 * The history under a board or a tree, kept in step with what is dragged on it.
 *
 * Most changes of the administration are forms, and a form reloads the page
 * it ends on - the history under it is new along with everything else. A
 * drag is not a form. A task pushed across its board or a wiki page dragged
 * into another place is saved by a request of its own and the page stays
 * where it is, so the history under it would go on showing the world before
 * the drag until somebody pressed reload.
 *
 * So a screen which saves something without leaving says so, and the panel
 * asks the portal for itself again: the same address the page came from - the
 * same page of the history, too - read in the background, and only the panel
 * taken out of the answer. The rest of the screen is left exactly as it is,
 * the drag in progress included.
 */

import { query } from "../core/dom";

/** What a screen raises after it saved something without reloading. */
export const HISTORY_CHANGED = "history:changed";

/** The panel of the history a section keeps on its own screen. */
const PANEL = "[data-own-history]";

/** How long a burst of saves is waited out, so one drag asks once. */
const SETTLE_MS = 250;

/** Say that something was saved without the page reloading. */
export function announceHistoryChange(): void {
    document.dispatchEvent(new CustomEvent(HISTORY_CHANGED));
}

/**
 * Put the panel found in *html* in place of *panel*, and return whether it did.
 *
 * Nothing is replaced when the answer has no panel - a page which answered
 * with an error, or with the login screen, is not a history.
 */
export function swapPanel(panel: HTMLElement, html: string): boolean {
    const fresh = new DOMParser().parseFromString(html, "text/html").querySelector<HTMLElement>(PANEL);

    if (!fresh) {
        return false;
    }

    panel.replaceWith(document.importNode(fresh, true));

    return true;
}

/** Ask the portal for the panel again and draw it. */
export async function refreshPanel(address: string = window.location.href): Promise<boolean> {
    const panel = query<HTMLElement>(PANEL);
    if (!panel) {
        return false;
    }

    const response = await fetch(address, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
    });

    if (!response.ok) {
        return false;
    }

    // The panel is looked up again: another refresh may have replaced it
    // while this one was out.
    const current = query<HTMLElement>(PANEL);

    return current ? swapPanel(current, await response.text()) : false;
}

/** Keep the history of the current screen in step with what is saved on it. */
export function initOwnHistory(): void {
    if (!query(PANEL)) {
        return;
    }

    let waiting: number | undefined;

    document.addEventListener(HISTORY_CHANGED, () => {
        window.clearTimeout(waiting);
        waiting = window.setTimeout(() => {
            void refreshPanel().catch(() => undefined);
        }, SETTLE_MS);
    });
}
